import { vec2 } from 'gl-matrix';

// Function to march through the grid and generate polygons
// The cases used in the lookup can be found in https://en.wikipedia.org/wiki/Marching_squares "Look-up table contour lines"
export function marchingSquares(
    grid: ArrayLike<number>,
    width: number,
    height: number,
    resx: number,
    resy: number,
    threshold: number
): vec2[][] {
    // resulting polygons
    const edges: Edge[] = [];

    const handleEdge = (p1: vec2, p2: vec2) => {
        edges.push([p1, p2]);
    };

    const sampleGrid = (x: number, y: number) => {
        if (x < 0 || x >= width || y < 0 || y >= height) {
            return 0;
        }
        return grid[y * width + x];
    };

    // expand the grid by one pixel in each direction
    for (let y = -1; y < height; y++) {
        for (let x = -1; x < width; x++) {
            const nw = sampleGrid(x, y);
            const ne = sampleGrid(x + 1, y);
            const se = sampleGrid(x + 1, y + 1);
            const sw = sampleGrid(x, y + 1);

            const caseValue = pixelsToCase(
                nw >= threshold,
                ne >= threshold,
                se >= threshold,
                sw >= threshold
            );

            if (caseValue === 0 || caseValue === 15) {
                // no edge found
                continue;
            }

            // edges go clockwise from top
            const a = vec2.fromValues(
                x * resx + resx * lerp(threshold, nw, ne),
                y * resy
            );
            const b = vec2.fromValues(
                x * resx + resx,
                y * resy + resy * lerp(threshold, ne, se)
            );
            const c = vec2.fromValues(
                x * resx + resx * lerp(threshold, sw, se),
                y * resy + resy
            );
            const d = vec2.fromValues(
                x * resx,
                y * resy + resy * lerp(threshold, nw, sw)
            );
            switch (caseValue) {
                case 1:
                    handleEdge(d, c);
                    break;
                case 14:
                    handleEdge(c, d);
                    break;
                case 2:
                    handleEdge(c, b);
                    break;
                case 13:
                    handleEdge(b, c);
                    break;
                case 3:
                    handleEdge(d, b);
                    break;
                case 12:
                    handleEdge(b, d);
                    break;
                case 11:
                    handleEdge(b, a);
                    break;
                case 4:
                    handleEdge(b, a);
                    break;
                case 5:
                    handleEdge(d, a);
                    handleEdge(b, c);
                    break;
                case 6:
                    handleEdge(c, a);
                    break;
                case 9:
                    handleEdge(a, c);
                    break;

                case 7:
                    handleEdge(d, a);
                    break;
                case 8:
                    handleEdge(a, d);
                    break;
                case 10:
                    handleEdge(a, b);
                    handleEdge(c, d);
                    break;
                default:
                    break;
            }
        }
    }
    if (edges.length === 0) {
        return [];
    }
    const polygons: vec2[][] = [];
    const visitedEdges = new Set<number>();
    for (let i = 0; i < edges.length; i++) {
        if (visitedEdges.has(i)) {
            continue;
        }
        const startingEdge = edges[i];
        const polygon: vec2[] = [];
        visitedEdges.add(i);
        polygon.push(startingEdge[0]);
        polygon.push(startingEdge[1]);
        let currentPoint = startingEdge[1];
        while (!vec2.equals(currentPoint, polygon[0])) {
            const nextEdgeIdx = edges.findIndex(
                (edge, idx) =>
                    !visitedEdges.has(idx) && edgeConnects(currentPoint, edge)
            );
            if (nextEdgeIdx === -1) {
                console.warn(
                    `disconnected polygon at ${currentPoint}`,
                    polygon
                );
                break;
            }
            const nextEdge = edges[nextEdgeIdx];
            const nextPoint = vec2.equals(currentPoint, nextEdge[0])
                ? nextEdge[1]
                : nextEdge[0];
            polygon.push(nextPoint);
            currentPoint = nextPoint;
            visitedEdges.add(nextEdgeIdx);
        }
        polygons.push(polygon);
    }
    return polygons;
}

type Edge = [vec2, vec2];

function lerp(x: number, x0: number, x1: number, y0 = 0, y1 = 1) {
    if (x0 === x1) {
        return x0;
    }

    return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

function pixelsToCase(nw: boolean, ne: boolean, se: boolean, sw: boolean) {
    const a = [Number(nw), Number(ne), Number(se), Number(sw)];
    return a.reduce((res, x) => (res << 1) | x);
}

function edgeConnects(point: vec2, edge: Edge): boolean {
    return vec2.equals(point, edge[0]) || vec2.equals(point, edge[1]);
}

// line simplification algorithm based on simplify-js

/**
 * Simplifies a given array of points using the Ramer-Douglas-Peucker algorithm.
 */
export function simplify(
    points: vec2[],
    tolerance: number = 1,
    highestQuality: boolean = false
) {
    if (points.length <= 2) return points;

    const sqTolerance = tolerance * tolerance;

    points = highestQuality ? points : simplifyRadialDist(points, sqTolerance);
    points = simplifyDouglasPeucker(points, sqTolerance);

    return points;
}

// square distance between 2 points
function getSqDist(p1: vec2, p2: vec2) {
    const dx = p1[0] - p2[0],
        dy = p1[1] - p2[1];
    return dx * dx + dy * dy;
}

// square distance from a point to a segment
function getSqSegDist(p: vec2, p1: vec2, p2: vec2) {
    let x = p1[0],
        y = p1[1],
        dx = p2[0] - x,
        dy = p2[1] - y;

    if (dx !== 0 || dy !== 0) {
        const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);

        if (t > 1) {
            x = p2[0];
            y = p2[1];
        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }

    dx = p[0] - x;
    dy = p[1] - y;

    return dx * dx + dy * dy;
}

// basic distance-based simplification
function simplifyRadialDist(points: vec2[], sqTolerance: number) {
    if (points.length <= 2) return points;
    let prevPoint = points[0];
    const newPoints = [prevPoint];
    let point!: vec2;

    for (let i = 1, len = points.length; i < len; i++) {
        point = points[i];

        if (getSqDist(point, prevPoint) > sqTolerance) {
            newPoints.push(point);
            prevPoint = point;
        }
    }

    if (prevPoint !== point) newPoints.push(point);

    return newPoints;
}

function simplifyDPStep(
    points: vec2[],
    first: number,
    last: number,
    sqTolerance: number,
    simplified: vec2[]
) {
    let maxSqDist = sqTolerance,
        index!: number;

    for (let i = first + 1; i < last; i++) {
        const sqDist = getSqSegDist(points[i], points[first], points[last]);

        if (sqDist > maxSqDist) {
            index = i;
            maxSqDist = sqDist;
        }
    }

    if (maxSqDist > sqTolerance) {
        if (index - first > 1)
            simplifyDPStep(points, first, index, sqTolerance, simplified);
        simplified.push(points[index]);
        if (last - index > 1)
            simplifyDPStep(points, index, last, sqTolerance, simplified);
    }
}

// simplification using Ramer-Douglas-Peucker algorithm
function simplifyDouglasPeucker(points: vec2[], sqTolerance: number) {
    const last = points.length - 1;

    const simplified = [points[0]];
    simplifyDPStep(points, 0, last, sqTolerance, simplified);
    simplified.push(points[last]);

    return simplified;
}
